import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { SearchMultiselectComponent } from '@app/shared/components/search-multiselect/search-multiselect.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';

import { SelectOption } from './select-option';

@Component({
  selector: 'am-multiselect-with-search',
  templateUrl: './multiselect-with-search.component.html',
  styleUrl: './multiselect-with-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MultiselectWithSearchComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    MatFormField,
    MatSelect,
    ReactiveFormsModule,
    SearchMultiselectComponent,
    MatCheckbox,
    MatOption,
    AsyncPipe,
  ],
  standalone: true,
})
export class MultiselectWithSearchComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label?: string;

  @Input() options: SelectOption[];

  @ViewChild('select') select: MatSelect;

  multiSelectCtrl = new FormControl([]);

  searchCtrl = new FormControl();

  filteredOptions$: Observable<SelectOption[]>;

  parentHasBeenReset$ = new BehaviorSubject<void>(null);

  toggleOptions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _filteredOptions$: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);

  private _destroyRef = inject(DestroyRef);

  onChanged: any = () => {
    /* */
  };
  onTouched: any = () => {
    /* */
  };

  ngOnInit(): void {
    this.toggleOptions$.next(false);
    this.filteredOptions$ = this._filteredOptions$.asObservable();

    this.searchCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._filterOptions();
    });

    this.multiSelectCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(values => {
      const currentToggleState = this.toggleOptions$.value;
      const newToggleState = values?.length === this.options.length;
      if (currentToggleState !== newToggleState) {
        this.toggleOptions$.next(newToggleState);
      }
      this.onChanged(values);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { options } = changes;

    if (options?.currentValue) {
      this._filteredOptions$.next(options.currentValue);
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(inputValue: number[]): void {
    if (inputValue == null) {
      return;
    }
    if (Array.isArray(inputValue) && inputValue.length === 0) {
      this.parentHasBeenReset$.next();
      this.multiSelectCtrl.reset();
      this.searchCtrl.reset();
    } else {
      this.multiSelectCtrl.setValue(inputValue);
    }

    this.toggleOptions$.next(inputValue.length === this.options.length);
  }

  setDisabledState(isDisabled: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isDisabled ? this.multiSelectCtrl.disable() : this.multiSelectCtrl.enable();
  }

  toggleAll(): void {
    if (this.toggleOptions$.value) {
      this.multiSelectCtrl.setValue([]);
      this.toggleOptions$.next(false);
    } else {
      this.multiSelectCtrl.setValue(this.options.map(option => option.value));
      this.toggleOptions$.next(true);
    }
  }

  private _filterOptions(): void {
    if (!this.options) {
      return;
    }
    const search = this.searchCtrl.value;
    if (!search) {
      this._filteredOptions$.next(this.options.slice());
      return;
    }
    const lowerCaseSearch = search.toLowerCase();
    const filteredOptions = this.options.filter(option => option.label?.toLowerCase().indexOf(lowerCaseSearch) > -1);
    this._filteredOptions$.next(filteredOptions);
  }
}
